import { css } from "@compiled/react";
import { DEFAULT_VALUES } from "@libraries/web-framework";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { Image } from "@/components/wpl/image/src";

import { getDevOpsSectionImage } from "../localized-images";

const devOpsSectionStyle = css({
    "backgroundImage":
        "url(https://wac-cdn.atlassian.com/dam/jcr:50bd2604-7910-4379-909b-a30192bee6c9/developer-bg.svg?cdnVersion=1195)",
    "backgroundPosition": "center",
    "backgroundRepeat": "no-repeat",
    "backgroundSize": "cover",
    "color": "#ffffff",
    "padding": "96px 96px 96px 0px",
    ".content-container": {
        padding: "16px 0",
        h2: {
            fontWeight: "normal",
            [media.above.sm]: {
                fontSize: "2.25rem"
            },
            [media.below.sm]: {
                fontSize: "1.5rem"
            }
        }
    },
    ".benefits-container": {
        "padding": "64px 0",
        "h3": {
            margin: "0 0 10px",
            fontSize: "0.75rem",
            fontWeight: "normal",
            textTransform: "uppercase"
        },

        ".contentBlock-body": {
            fontSize: "1rem",
            fontWeight: "normal",
            lineHeight: "1.5"
        },

        "h4": {
            fontSize: "2.25rem",
            fontWeight: "500",
            lineHeight: "0.3",
            padding: `${token("space.100")} 0 ${token("space.200")}`,
            margin: "0",

            p: {
                fontSize: "1.25rem",
                fontWeight: "400",
                letterSpacing: "0.3px",
                lineHeight: "1.4"
            }
        }
    },
    ".desktop-img": {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "16px 0"
    },
    ".mobile-img": {
        display: "none",
        flexDirection: "column",
        justifyContent: "center",
        padding: "16px 0"
    },

    ".dev-ops-content": {
        padding: "32px 0"
    },

    ".company-image-container": {
        marginBlockEnd: "16px",
        width: "150px"
    },

    [media.above.sm]: {
        ".section-container": {
            width: "90%",
            maxWidth: "1320px",
            margin: "auto",
            display: "grid",
            gridTemplateColumns: "5fr 3fr",
            gridColumnGap: "8%"
        }
    },

    [media.below.sm]: {
        "padding": `${token("space.200")}`,

        ".desktop-img": {
            display: "none"
        },

        ".mobile-img": {
            display: "block"
        }
    }
});

const DevOpsSection = () => {
    const intl = useIntl();

    return (
        <section css={devOpsSectionStyle}>
            <div className="section-container">
                {/* only renders on larger screens */}
                <div className="desktop-img">
                    <DevOpsImage />
                </div>
                <div className="content-container">
                    <div className="dev-ops-content">
                        <ContentBlock
                            headerType="h2"
                            headerText={
                                <FormattedMessage
                                    id="JiraSoftwarePageTemplate.devOpsSection.header"
                                    defaultMessage="Let developers focus on code"
                                    values={DEFAULT_VALUES}
                                />
                            }
                            bodyText={
                                <FormattedMessage
                                    id="JiraSoftwarePageTemplate.devOpsSection.body"
                                    defaultMessage="Developers want to focus on code, not update issues. We get it! Open DevOps makes it easier to do both regardless of the tools you use. Now developers can stay focused and the business can stay aligned."
                                    values={DEFAULT_VALUES}
                                />
                            }
                            ctaProps={{
                                buttonProps: {
                                    text: intl.formatMessage({
                                        id: "JiraSoftwarePageTemplate.devOpsSection.cta",
                                        defaultMessage: "Explore Open DevOps"
                                    }),
                                    url: "https://www.atlassian.com/solutions/devops",
                                    type: "secondary",
                                    color: "white",
                                    size: "small",
                                    dataEventProps: {
                                        "data-event-component": "linkButton",
                                        "data-event-container": "devOpsSection",
                                        "data-event": "clicked",
                                        "data-product-key": "jiraSoftware"
                                    }
                                }
                            }}
                        />
                    </div>

                    {/* only renders on smaller screens */}
                    <div className="mobile-img">
                        <DevOpsImage />
                    </div>

                    <div className="benefits-container">
                        <h3>
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.devOpsSection.benefits"
                                defaultMessage="How customers are benefiting"
                                values={DEFAULT_VALUES}
                            />
                        </h3>
                        <div className="company-image-container">
                            <Image
                                src="https://wac-cdn.atlassian.com/dam/jcr:c137f24d-10a9-473e-bbcc-0fababfb9857/FLO_logo_horizontal_white%201.png?cdnVersion=1196"
                                alt={intl.formatMessage({
                                    id: "JiraSoftwarePageTemplate.devOpsSection.flowLogoAlt",
                                    defaultMessage: "Flow company logo"
                                })}
                            />
                        </div>
                        <h4>
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.devOpsSection.floIncreasePercent"
                                defaultMessage="900%"
                                values={DEFAULT_VALUES}
                            />
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.devOpsSection.floIncreaseMessage"
                                defaultMessage="<p>increase in deployments</p>"
                                values={DEFAULT_VALUES}
                            />
                        </h4>
                        <h4>
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.devOpsSection.floDecreasePercent"
                                defaultMessage="50%"
                            />
                            <FormattedMessage
                                id="JiraSoftwarePageTemplate.devOpsSection.floDecreaseMessage"
                                defaultMessage="<p>decrease in cycle time</p>"
                                values={DEFAULT_VALUES}
                            />
                        </h4>
                    </div>
                </div>
            </div>
        </section>
    );
};

const DevOpsImage = () => {
    const intl = useIntl();
    const localizedImageSrc = getDevOpsSectionImage(intl.locale);
    return (
        <Image
            src={localizedImageSrc}
            alt={intl.formatMessage({
                id: "JiraSoftwarePageTemplate.devOpsSection.imageAlt",
                defaultMessage:
                    "Jira Software DevOps diagram: Plan, code, deploy, operate, and collaborate"
            })}
        />
    );
};

export default DevOpsSection;
